/**
 * Lazy PaymentMethodModal wrapper
 *
 * Defers loading `@stripe/stripe-js` and `@stripe/react-stripe-js` until the
 * modal is opened for the first time. Keeps Stripe setup code off the browse
 * bundle (same pattern as LazyUpgradeModal — PBI #1137 / #1181).
 */

import { Suspense, lazy, useEffect, useState } from 'react';
import type { PaymentMethodModalProps } from './PaymentMethodModal';

const PaymentMethodModalLazy = lazy(() =>
  import('./PaymentMethodModal').then((m) => ({ default: m.PaymentMethodModal })),
);

/**
 * Renders nothing until `opened` becomes true once, then keeps the lazy chunk
 * mounted so subsequent opens do not re-fetch.
 */
export function LazyPaymentMethodModal(props: PaymentMethodModalProps) {
  const [shouldLoad, setShouldLoad] = useState(props.opened);

  useEffect(() => {
    if (props.opened) {
      setShouldLoad(true);
    }
  }, [props.opened]);

  if (!shouldLoad) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <PaymentMethodModalLazy {...props} />
    </Suspense>
  );
}
